<template>
  <main id="app" class="app-grid">
    <router-view @banner="bannerChanged" @darksec="darkSection" />
    <Navigation
      id="header"
      class="work-nav"
      :class="{ dark: invertText, unfolded: !isFolded, folded: isFolded }"
      :sections="{}"
      :otherRoutes="otherRoutes"
      currentSection="work"
      :showHome="true"
      :wasHome="wasHome"
      :showRule="true"
      :showLink="!bannerHidden"
      :sticky="true"
      backAlt="Back to Works"
    >
      <div class="work-link">
        <hr :class="{ 'work-link-show': bannerHidden }" :style="linkRules" />
        <p :class="{ 'work-link-show': bannerHidden }">
          {{ currentWorkTitle }}
        </p>
      </div>

      <template #out>
        <button id="to-top" class="to-top action" v-scroll-to="'#banner'">
          top
        </button>
      </template>
    </Navigation>
    <div class="fold-menu" :class="{ unfolded: !isFolded, folded: isFolded }">
      <button id="button-fold" class="button-fold action" @click="handleFold">
        <ion-icon name="arrow-back" size="large" v-pre>Fold Menu</ion-icon>
      </button>
      <button
        id="button-unfold"
        class="button-unfold action"
        @click="handleUnfold"
      >
        <ion-icon name="menu" size="large" v-pre>Menu</ion-icon>
      </button>
    </div>
    <section id="project-nav" class="project-nav">
      <div class="project-link left">
        <p class="overline text-second margin1-t">Previous</p>
        <router-link
          v-if="lastWork"
          :to="lastWork.url"
          class="h3 left-arrow"
          v-html="lastWork.title"
        />
        <p v-else class="project-placeholder">This is the first project.</p>
      </div>
      <div class="project-link center back-home">
        <hr />
        <router-link to="/" class="overline margin3-b">Back Home</router-link>
      </div>
      <div class="project-link right">
        <p class="overline text-second margin1-t">Next</p>
        <router-link
          v-if="nextWork"
          :to="nextWork.url"
          class="h3 right-arrow"
          v-html="nextWork.title"
        />
        <p v-else class="project-placeholder">This is the last project.</p>
      </div>
    </section>
    <Footer id="footer" :leftCopy="false" />
  </main>
</template>

<style lang="scss">
@import '@/styles/base.scss';

.banner {
  grid-row: 1 / span 1;
  padding-top: $banner-padding-top;
  @extend .padding4-b;

  .stroke {
    z-index: 12;
  }

  .title {
    grid-column: 2 / -2;
    grid-row: 1 / span 1;
    margin-top: 0;
    margin-bottom: 0;
    z-index: 5;
  }
  .sub-headline {
    grid-column: 2 / -2;
    // grid-row: 2 / span 1;
    padding: $baseline * 0.5 0 $baseline * 1.5;
    margin-top: 0 !important;
    z-index: 5;
    @extend .margin1-b;
  }
  .summary {
    grid-column: 2 / -2;
    z-index: 5;
    @extend .margin1;
  }

  .team {
    grid-column: 2 / span 2;
    @extend .margin1;
  }
  .tab-left {
    margin-left: $font-size;
  }

  .role {
    grid-column: 2 / span 2;
    @extend .margin1-b;
  }
}

.banner-bg {
  grid-column: 1 / -1;
  padding-top: $page-padding-top;
  pointer-events: none;
  margin: $baseline 0 0;
  position: relative;
  // z-index: -1;
  // margin-bottom: 0;
}
.banner-logo {
  position: absolute;
  width: 12.5vw;
  max-width: $column-width;
  right: $body-padding;
  top: $page-padding-top;
}

.dark-body {
  .headline,
  .keyline,
  .highlight {
    color: $text-dark;
  }
  .divider {
    background-color: $text-light-second;
  }
}

.work-nav {
  .to-top {
    position: fixed;
    color: $text-toned-down;
    text-transform: uppercase;
    bottom: $page-padding-bottom;
    text-align: center;

    &:before {
      content: '↑';
      display: block;
      text-align: center;
    }
  }
}

.work-link {
  display: grid;
  grid-template-columns: $body-padding - $logo-margin-link 1fr;
  grid-column-gap: $logo-margin-link;
  position: relative;
  left: -$body-padding;
  margin: 0 0 $line-height;

  hr {
    background-color: $text-light;
    margin: $line-height - $baseline * 0.5 - $logo-stroke 0 0 !important;
    height: $logo-stroke;
    width: 0;
    transition-property: width;
    transition-duration: 0.3s;
    transition-timing-function: ease-out;

    &.work-link-show {
      width: 100%;
    }
  }
  p {
    opacity: 0;
    margin: 0;
    width: $logo-width;
    font-weight: 500;
    transition-property: opacity;
    transition-duration: 0.3s;

    &.work-link-show {
      opacity: 1;
    }
  }
}

.project-nav {
  grid-column: 2 / -2;
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: $column-gap;
  @extend .padding1;

  a {
    display: block;
    text-decoration: none !important;
    // max-width: $column-width * 2 + $column-gap;
  }
  .project-placeholder {
    padding-top: $baseline * 0.75;
    padding-bottom: $baseline * 0.25;
    margin-bottom: $baseline;
  }
  .right-arrow:hover:before,
  .right-arrow:focus:before {
    content: '→';
    display: block;
    position: absolute;
    width: $line-height;
    right: -$line-height;
  }
  .left-arrow:hover:before,
  .left-arrow:focus:before {
    content: '←';
    display: block;
    position: absolute;
    width: $line-height;
    left: -$line-height;
  }

  .back-home {
    display: none;
  }
}

// Media query
@media only screen and (min-width: $small-width) {
  .banner {
    .title {
      grid-column-start: 3;
    }
    .sub-headline {
      grid-column-start: 3;
    }
    .summary {
      grid-column-start: 3;
    }
    .team {
      grid-column-start: 3;
    }
    .role {
      grid-column: 3 / span 3;
    }
  }
  .banner-bg {
    margin-top: 0;
  }
  .project-nav {
    grid-column-start: 3;
  }
}
@media only screen and (min-width: $mid-width) {
  .banner {
    .title {
      grid-column-end: span 3;
    }
    .sub-headline {
      grid-column-end: span 3;
    }
    .summary {
      grid-column-end: span 3;
    }
    .role {
      grid-column: 5 / span 3;
      margin-top: $line-height;
    }
  }
  .project-nav {
    grid-template-columns: repeat(3, 1fr);

    .back-home {
      display: block;
    }
  }
}
@media only screen and (min-width: $large-width) {
  .banner {
    .team {
      grid-column: 3 / span 2;
    }
    .role {
      grid-column: 5 / span 3;
    }
  }
  .project-nav {
    grid-column-end: -3;
  }
}
</style>

<script>
import Navigation from '@/components/Navigation.vue'
import Footer from '@/components/Footer.vue'
// import Rellax from 'rellax'

export default {
  name: 'work',
  components: {
    Navigation,
    Footer
  },
  data() {
    return {
      routes: this.$store.state.nav.routes,
      works: this.$store.state.nav.routes.work.routes,
      bannerHidden: false,
      invertText: false,
      isFolded: true
    }
  },
  computed: {
    lastWork() {
      let cur = this.$route
      let allWorks = this.works
      let workKeys = Object.keys(allWorks)
      let i = workKeys.indexOf(cur.name)
      if (i <= 0) {
        return null
      } else {
        return allWorks[workKeys[i - 1]]
      }
    },
    nextWork() {
      let cur = this.$route
      let allWorks = this.works
      let workKeys = Object.keys(allWorks)
      let i = workKeys.indexOf(cur.name)
      if (i == workKeys.length - 1) {
        return null
      } else {
        return allWorks[workKeys[i + 1]]
      }
    },
    currentWorkTitle() {
      return this.$route.meta.title
    },
    linkRules() {
      return (
        'background-color: ' + this.$store.state.nav.routes.work.linkColor + ';'
      )
    },
    otherRoutes() {
      let res = {}
      Object.assign(res, this.routes)
      delete res.home
      delete res.work
      delete res.explore
      return res
    },
    wasHome() {
      return this.$store.state.nav.lastRouteName === 'home'
    }
  },
  methods: {
    handleFold() {
      this.isFolded = true
    },
    handleUnfold() {
      this.isFolded = false
    },
    bannerChanged(isVisible) {
      if (isVisible === this.bannerHidden) {
        this.bannerHidden = !isVisible
      }
    },
    darkSection(isVisible) {
      if (isVisible !== this.invertText) {
        this.invertText = isVisible
      }
    }
  },
  mounted() {
    // new Rellax('.rellax')
  }
}
</script>
